<template>
  <div class="AppHeader">
    <span class="AppHeader__nav">
      <NuxtLink :to="dashboardUrl">
        <AppLogo data-testid="header-tui-logo" />
      </NuxtLink>
      <NovaTabs
        class="AppHeader__tabs ml-6"
        :options="options"
        :selected="selectedTab"
        :accent-width="30"
        @select:option="(ev) => $router.push(ev.value)"
      />
    </span>
    <div>
      <AppUserDropdown />
    </div>
  </div>
</template>

<script setup lang="ts">
import NovaTabs from "@/ui-kit/NovaTabs/NovaTabs.vue";
import AppLogo from "./AppLogo.vue";
import AppUserDropdown from "@/features/core-shared/components/AppUserDropdown.vue";
import { getDashboardUrl } from "@/features/experience-dashboard/lib/get-dashboard-url";
import { useTokenPermissions, TokenPermissionModule } from "@/features/token-permissions";

import type { TokenPermissionModuleType } from "@/features/token-permissions";

interface NavigationOption {
  title: string;
  value: string;
  requiredPermission?: TokenPermissionModuleType;
}

const { $t } = useNuxtApp();
const route = useRoute();
const { canAccess } = useTokenPermissions();

const navigationOptions: NavigationOption[] = [
  { title: $t("app-sidebar.tab.administration"), value: "/masterdata-management" },
  { title: $t("app-sidebar.tab.product"), value: "/" },
  {
    title: $t("app-sidebar.tab.suppliers"),
    value: "/suppliers/requests",
    requiredPermission: TokenPermissionModule.SUPPLIERS,
  },
  { title: $t("app-sidebar.tab.stop-sales"), value: "/stop-sales" },
  { title: $t("app-sidebar.tab.shared-allotment"), value: "/shared-allotment" },
];

const options = computed(() => {
  return navigationOptions.filter((option) => {
    if (option.requiredPermission) {
      return canAccess(option.requiredPermission);
    }
    return true;
  });
});

const dashboardUrl = getDashboardUrl();

const getSelectedTab = (path: string) => {
  const pathMatchers: Array<{ match: (p: string) => boolean; value: string }> = [
    { match: (p) => p.startsWith("/masterdata-management"), value: "/masterdata-management" },
    { match: (p) => p === "/", value: "/" },
    { match: (p) => p.startsWith("/suppliers"), value: "/suppliers/requests" },
    { match: (p) => p === "/stop-sales", value: "/stop-sales" },
    { match: (p) => p === "/shared-allotment", value: "/shared-allotment" },
  ];

  const matched = pathMatchers.find((matcher) => matcher.match(path));
  return matched ? options.value.find((opt) => opt.value === matched.value) : undefined;
};

const selectedTab = computed(() => getSelectedTab(route.path));
</script>

<style scoped lang="scss">
@import "@/assets/scss/utilities";

.AppHeader {
  position: fixed;
  z-index: var(--z-index-header);
  padding: 0 rem(16);
  width: 100%;
  height: var(--header-height);
  background: var(--color-white);
  border-bottom: var(--border-default);
  display: flex;
  align-items: center;
  justify-content: space-between;

  &__nav {
    display: flex;
    align-items: center;
    height: calc(var(--header-height) - 3px);
  }

  &__tabs {
    height: 100%;
    display: flex;
    gap: 20px;
  }
}
</style>
